import type { Pin } from '__types__';
import {
  CircleUserRound,
  Download,
  ExternalLink,
  MoreHorizontal,
  Tags,
  Trash2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { StopPropagation } from 'v5/platform/react/StopPropagation';
import { pinboardUtils } from './pinboard.logic';

/** How many of a pin's tags its caption names; the rest is "+N". */
const SHOWN_TAGS = 2;

/** A pin's tags at the end of its tile's caption line: the first ones, then how many more. */
export function PinTags({ pin }: { pin: Pin }) {
  const tags = pin.tags ?? [];
  if (tags.length === 0) return null;
  const rest = tags.length - SHOWN_TAGS;
  return (
    <span className="flex min-w-0 shrink-0 items-center gap-1">
      {tags.slice(0, SHOWN_TAGS).map(tag => (
        <Badge
          key={tag.id ?? tag.tag}
          variant="tint"
          size="sm"
          className="max-w-32 truncate text-white"
        >
          {tag.tag}
        </Badge>
      ))}
      {rest > 0 && (
        <Badge variant="tint" size="sm" className="text-white">
          +{rest}
        </Badge>
      )}
    </span>
  );
}

/**
 * A pin's actions over its tile's corner: edit tags, set as the profile picture, download or
 * open, delete. Modal: a click away only closes it.
 */
export function PinMenu({
  pin,
  onEditTags,
  onSetAsProfilePicture,
  onDelete
}: {
  pin: Pin;
  /** Absent where the pin can't be changed. */
  onEditTags?(): void;
  /** Absent where the pin can't be the profile's picture (not a picture, read only). */
  onSetAsProfilePicture?(): void;
  onDelete?(): void;
}) {
  const { t } = useTranslation('resources', { keyPrefix: 'Pinboard.card' });
  const name = pinboardUtils.nameOf(pin) ?? t('untitled');
  const href = pinboardUtils.hrefOf(pin);
  const download = pinboardUtils.openActionOf(pin) === 'download';
  return (
    // the menu is the tile's own: no press or key in it reaches the gallery
    <StopPropagation>
      <span className="flex">
        <DropdownMenu modal>
          <DropdownMenuTrigger asChild>
            <Button
              variant="overlay"
              size="xs"
              layout="icon"
              shape="rounded"
              aria-label={t('actions', { name })}
            >
              <MoreHorizontal className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onEditTags && (
              <DropdownMenuItem onSelect={onEditTags}>
                <Tags />
                {t('editTags')}
              </DropdownMenuItem>
            )}
            {onSetAsProfilePicture && (
              <DropdownMenuItem onSelect={onSetAsProfilePicture}>
                <CircleUserRound />
                {t('setAsProfilePicture')}
              </DropdownMenuItem>
            )}
            {href !== null && (
              <DropdownMenuItem asChild>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...(download ? { download: true } : {})}
                >
                  {download ? <Download /> : <ExternalLink />}
                  {download ? t('download') : t('openLink')}
                </a>
              </DropdownMenuItem>
            )}
            {onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={onDelete}
                  className="text-fg-danger focus:text-fg-danger"
                >
                  <Trash2 />
                  {t('delete')}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </StopPropagation>
  );
}
